import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/auth";
import { ThemeProvider } from "@/lib/theme";
import { Toaster } from "@/components/ui/sonner";

import Login from "@/pages/Login";
import AppLayout from "@/components/AppLayout";
import Dashboard from "@/pages/Dashboard";
import BedMap from "@/pages/BedMap";
import Admission from "@/pages/Admission";
import PatientList from "@/pages/PatientList";
import PatientDetail from "@/pages/PatientDetail";
import Admin from "@/pages/Admin";
import BillingConfig from "@/pages/BillingConfig";
import BillingDashboard from "@/pages/BillingDashboard";
import BillingReports from "@/pages/BillingReports";
import BillingAuditLog from "@/pages/BillingAuditLog";
import NursingDashboard from "@/pages/NursingDashboard";
import DoctorRounds from "@/pages/DoctorRounds";
import WardLayout from "@/components/ward/WardLayout";
import WardBills from "@/pages/ward/WardBills";
import WardBillEditor from "@/pages/ward/WardBillEditor";
import WardReport from "@/pages/ward/WardReport";
import "@/App.css";

function Protected({ children, roles }) {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role) && user.role !== "admin") return <Navigate to="/dashboard" replace />;
  return children;
}

function RootRedirect() {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (localStorage.getItem("ward_mode") === "1") return <Navigate to="/ward" replace />;
  if (user.role === "admin") return <Navigate to="/admin" replace />;
  if (user.role === "nurse") return <Navigate to="/nursing" replace />;
  if (user.role === "doctor") return <Navigate to="/rounds" replace />;
  return <Navigate to="/dashboard" replace />;
}

export default function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route element={<Protected><AppLayout /></Protected>}>
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/beds" element={<BedMap />} />
              <Route path="/patients" element={<PatientList />} />
              <Route path="/patients/:id" element={<PatientDetail />} />
              <Route path="/admit" element={<Protected roles={["receptionist", "doctor", "nurse"]}><Admission /></Protected>} />
              <Route path="/nursing" element={<Protected roles={["nurse", "doctor"]}><NursingDashboard /></Protected>} />
              <Route path="/rounds" element={<Protected roles={["doctor"]}><DoctorRounds /></Protected>} />
              <Route path="/admin" element={<Protected roles={["admin"]}><Admin /></Protected>} />
              <Route path="/billing" element={<Protected roles={["admin", "receptionist"]}><BillingDashboard /></Protected>} />
              <Route path="/billing/reports" element={<Protected roles={["admin", "receptionist"]}><BillingReports /></Protected>} />
              <Route path="/billing/audit" element={<Protected roles={["admin", "receptionist"]}><BillingAuditLog /></Protected>} />
              <Route path="/billing/config" element={<Protected roles={["admin"]}><BillingConfig /></Protected>} />
            </Route>
            <Route element={<Protected><WardLayout /></Protected>}>
              <Route path="/ward" element={<WardBills />} />
              <Route path="/ward/bills/new" element={<WardBillEditor />} />
              <Route path="/ward/bills/:id" element={<WardBillEditor />} />
              <Route path="/ward/reports" element={<WardReport />} />
            </Route>
            <Route path="*" element={<RootRedirect />} />
          </Routes>
          <Toaster position="top-right" richColors />
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}
